// Encrypts every localStorage value at rest so DevTools shows ciphertext instead of e-mails, roles
// and user ids. This is obfuscation, not access control. The key is this tab's session key from auth
// (sessionStorage mspSk, see common-rest/cryptoKeys.ts), so no key ships in the bundle. Before login
// there is no key: values stay plaintext and are sealed once a key arrives (sealStorage).
// localStorage is synchronous, so this uses the `crypto` polyfill (crypto-browserify GCM, as
// cryptoUrl.ts) instead of the async Web Crypto API. See docs/LOCAL-STORAGE-ENCRYPTION.md.
import crypto from 'crypto'

export const STORAGE_PREFIX = 'enc2:'
// Written by builds that used the master key; nothing can decrypt them any more.
export const LEGACY_PREFIX = 'enc1:'
const IV_BYTES = 12
const TAG_BYTES = 16

// Binding the key name means a value copied under another key no longer decrypts.
function aad(name: string) {
    return Buffer.from('storage-v2:' + name, 'utf8')
}

function toBase64Url(buf: Buffer) {
    return buf.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(text: string) {
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/')
    return Buffer.from(b64 + '==='.slice((b64.length + 3) % 4), 'base64')
}

export function encryptValue(name: string, plaintext: string, key: Buffer): string {
    const iv = crypto.randomBytes(IV_BYTES)
    const cipher = crypto.createCipheriv('aes-256-gcm', key, iv)
    cipher.setAAD(aad(name))
    const content = Buffer.concat([cipher.update(Buffer.from(plaintext, 'utf8')), cipher.final()])
    return STORAGE_PREFIX + toBase64Url(Buffer.concat([iv, content, cipher.getAuthTag()]))
}

export function decryptValue(name: string, raw: string, key: Buffer): string {
    const bytes = fromBase64Url(raw.slice(STORAGE_PREFIX.length))
    if (bytes.length < IV_BYTES + TAG_BYTES) throw new Error('Encrypted storage value is too short')
    const decipher = crypto.createDecipheriv('aes-256-gcm', key, bytes.slice(0, IV_BYTES))
    decipher.setAAD(aad(name))
    decipher.setAuthTag(bytes.slice(bytes.length - TAG_BYTES))
    const content = bytes.slice(IV_BYTES, bytes.length - TAG_BYTES)
    return Buffer.concat([decipher.update(content), decipher.final()]).toString('utf8')
}

const STATE = '__mspStorageEncryption'

export interface StorageEncryptionOptions {
    getKey: () => Buffer | null
    enabled: boolean
    exclude: string[]
}

interface StorageWindow {
    Storage: { prototype: Storage }
    localStorage: Storage
}

interface StorageState {
    storage: Storage
    nativeGet: (name: string) => string | null
    nativeRemove: (name: string) => void
    excluded: (name: string) => boolean
    storeQuietly: (name: string, plain: string) => void
}

function localStorageOf(win: StorageWindow): Storage | null {
    try {
        return win.localStorage
    } catch (e) {
        return null
    }
}

function storedNames(proto: any, storage: Storage): string[] {
    const names: string[] = []
    for (let i = 0; i < storage.length; i++) names.push(proto.key.call(storage, i))
    return names
}

// Patches Storage.prototype for window.localStorage only (sessionStorage is untouched). Must run
// before any module reads localStorage: src/storageBootstrap.ts is the first import of src/index.tsx.
export function installStorageEncryption(win: StorageWindow, options: StorageEncryptionOptions): boolean {
    const storage = localStorageOf(win)
    const proto: any = win.Storage && win.Storage.prototype
    if (!storage || !proto || proto[STATE]) return false
    const nativeGet = proto.getItem
    const nativeSet = proto.setItem
    const nativeRemove = proto.removeItem
    storedNames(proto, storage).forEach(name => {
        const raw = nativeGet.call(storage, name)
        if (raw !== null && raw.indexOf(LEGACY_PREFIX) === 0) nativeRemove.call(storage, name)
    })

    // Debugging switch (REACT_APP_ENCRYPT_STORAGE=false): store plaintext again. Not a rollback step,
    // see docs/LOCAL-STORAGE-ENCRYPTION.md.
    if (!options.enabled) {
        const key = options.getKey()
        storedNames(proto, storage).forEach(name => {
            const raw = nativeGet.call(storage, name)
            if (raw === null || raw.indexOf(STORAGE_PREFIX) !== 0) return
            try {
                if (!key) throw new Error('No session key')
                nativeSet.call(storage, name, decryptValue(name, raw, key))
            } catch (e) {
                nativeRemove.call(storage, name)
            }
        })
        return false
    }

    const excluded = (name: string) => options.exclude.indexOf(name) !== -1
    // name -> {raw, plain}: decrypt each stored value once; a different raw (another tab) misses.
    const cache: { [name: string]: { raw: string, plain: string } } = Object.create(null)
    const store = (name: string, plain: string, key: Buffer) => {
        const raw = encryptValue(name, plain, key)
        nativeSet.call(storage, name, raw)
        cache[name] = { raw, plain }
    }
    // Sealing existing plaintext must never break start-up (e.g. quota exceeded): keep the plaintext
    // readable and remember it so the failure is logged once, not on every read.
    const storeQuietly = (name: string, plain: string) => {
        const key = options.getKey()
        if (!key) return
        try {
            store(name, plain, key)
        } catch (e) {
            cache[name] = { raw: plain, plain }
            console.error('localStorage encryption could not re-encrypt "' + name + '"', e)
        }
    }
    const state: StorageState = {
        storage,
        nativeGet,
        nativeRemove: (name: string) => nativeRemove.call(storage, name),
        excluded,
        storeQuietly
    }
    proto[STATE] = state
    sealStorage(win)

    proto.getItem = function (this: Storage, name: string) {
        if (this !== storage || arguments.length < 1) return nativeGet.apply(this, arguments)
        const k = String(name)
        const raw: string | null = nativeGet.call(storage, k)
        if (raw === null || excluded(k)) return raw
        const hit = cache[k]
        if (hit && hit.raw === raw) return hit.plain
        if (raw.indexOf(LEGACY_PREFIX) === 0) {
            nativeRemove.call(storage, k)
            return null
        }
        if (raw.indexOf(STORAGE_PREFIX) !== 0) {
            storeQuietly(k, raw)
            return raw
        }
        const key = options.getKey()
        // Sealed under a session this tab has no key for (yet): keep it for when the key arrives.
        if (!key) return null
        try {
            const plain = decryptValue(k, raw, key)
            cache[k] = { raw, plain }
            return plain
        } catch (e) {
            delete cache[k]
            nativeRemove.call(storage, k)
            return null
        }
    }
    proto.setItem = function (this: Storage, name: string, value: string) {
        if (this !== storage || arguments.length < 2) return nativeSet.apply(this, arguments)
        const k = String(name)
        const key = options.getKey()
        if (excluded(k) || !key) {
            delete cache[k]
            return nativeSet.call(storage, k, value)
        }
        store(k, String(value), key)
    }
    return true
}

// Encrypts values still stored as plaintext (written before login) with the current session key.
// Called by cryptoKeys.saveSession once a key arrives. No-op when not installed or there is no key.
// An enc1: value (an older build in another tab) is removed, never sealed as if it were plaintext.
export function sealStorage(win: { Storage: { prototype: Storage } }) {
    const proto: any = win && win.Storage && win.Storage.prototype
    const state: StorageState | undefined = proto && proto[STATE]
    if (!state) return
    storedNames(proto, state.storage).forEach(name => {
        const raw = state.nativeGet.call(state.storage, name)
        if (raw === null || state.excluded(name) || raw.indexOf(STORAGE_PREFIX) === 0) return
        if (raw.indexOf(LEGACY_PREFIX) === 0) state.nativeRemove(name)
        else state.storeQuietly(name, raw)
    })
}
